/**
 * Utilitário para gerenciar parâmetros de URL
 * 
 * Centraliza a lógica de detecção e manipulação de parâmetros
 * específicos na URL, além de permitir persistência via sessionStorage.
 */

// Chaves para armazenamento no sessionStorage
export const STORAGE_KEYS = {
  MARKETPLACE_SUCCESS: 'fluxmed_marketplace_success_modal',
  MARKETPLACE_ERROR: 'fluxmed_marketplace_error_modal',
  MARKETPLACE_ERROR_TYPE: 'fluxmed_marketplace_error_type',
};

// Tipos de mensagens do marketplace
export const MARKETPLACE_MESSAGE_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
};

// Tipos específicos de erros do marketplace
export const MARKETPLACE_ERROR_TYPES = {
  INVALID_PARAMETERS: 'invalid_parameters',
  ALREADY_AUTHORIZED: 'already_authorized',
  AUTHORIZATION_FAILED: 'authorization_failed',
  UNKNOWN_ERROR: 'unknown_error',
};

/**
 * Obtém o valor de um parâmetro específico da URL
 * 
 * @param paramName Nome do parâmetro a buscar
 * @returns {string|null} Valor do parâmetro ou null se não encontrado
 */
export function getUrlParamValue(paramName: string): string | null {
  const currentUrl = window.location.href;
  
  // Padrões para diferentes formatos de parâmetros na URL
  const patterns = [
    `[?&]${paramName}=([^&#]*)`,  // Formato padrão: ?param=value ou &param=value
    `#/?${paramName}=([^&#]*)`,   // Formato com hash: #/?param=value
    `#?${paramName}=([^&#]*)`     // Formato alternativo com hash: #?param=value
  ];
  
  // Tenta cada padrão
  for (const pattern of patterns) {
    const regex = new RegExp(pattern);
    const match = regex.exec(currentUrl);
    
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
  }
  
  return null;
}

/**
 * Verifica se a URL atual contém um parâmetro específico
 * 
 * @param paramName Nome do parâmetro a verificar
 * @returns {boolean} Verdadeiro se o parâmetro estiver presente
 */
export function hasUrlParam(paramName: string): boolean {
  const currentUrl = window.location.href;
  
  return (
    currentUrl.includes(`${paramName}=`) ||
    currentUrl.includes(`?${paramName}=`) ||
    currentUrl.includes(`&${paramName}=`) ||
    currentUrl.includes(`#/?${paramName}=`) ||
    currentUrl.includes(`#?${paramName}=`)
  );
}

/**
 * Verifica se um parâmetro específico com um valor específico está presente na URL
 * 
 * @param paramName Nome do parâmetro
 * @param paramValue Valor esperado do parâmetro
 * @returns {boolean} Verdadeiro se o parâmetro com o valor especificado estiver presente
 */
export function hasUrlParamWithValue(paramName: string, paramValue: string): boolean {
  const currentUrl = window.location.href;
  
  return (
    currentUrl.includes(`${paramName}=${paramValue}`) ||
    currentUrl.includes(`?${paramName}=${paramValue}`) ||
    currentUrl.includes(`&${paramName}=${paramValue}`) ||
    currentUrl.includes(`#/?${paramName}=${paramValue}`) ||
    currentUrl.includes(`#?${paramName}=${paramValue}`)
  );
}

/**
 * Remove um parâmetro da URL atual
 * 
 * @param paramName Nome do parâmetro a remover
 * @param paramValue Valor do parâmetro (opcional)
 * @returns {boolean} Verdadeiro se o parâmetro foi removido com sucesso
 */
export function removeUrlParam(paramName: string, paramValue?: string): boolean {
  try {
    const currentUrl = window.location.href;
    let newUrl = currentUrl;
    const valuePattern = paramValue ? `=${paramValue}` : '=[^&]*';
    
    // Diferentes padrões de parâmetros na URL
    const patterns = [
      `\\?${paramName}${valuePattern}&`, // ?param=value&
      `&${paramName}${valuePattern}&`,   // &param=value&
      `\\?${paramName}${valuePattern}`,  // ?param=value (fim da URL)
      `&${paramName}${valuePattern}`     // &param=value (fim da URL)
    ];
    
    // Substitui cada padrão possível
    patterns.forEach(pattern => {
      const regex = new RegExp(pattern);
      if (regex.test(newUrl)) {
        // Se for o formato ?param=value&, substitui por ?
        if (pattern === `\\?${paramName}${valuePattern}&`) {
          newUrl = newUrl.replace(regex, '?');
        } 
        // Se for o formato &param=value&, substitui por &
        else if (pattern === `&${paramName}${valuePattern}&`) {
          newUrl = newUrl.replace(regex, '&');
        }
        // Se for o último parâmetro, remove completamente
        else {
          newUrl = newUrl.replace(regex, '');
        }
      }
    });
    
    // Atualiza a URL sem recarregar a página
    window.history.replaceState({}, document.title, newUrl);
    
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Salva um sinalizador no sessionStorage
 * 
 * @param key Chave para armazenamento
 * @param value Valor a armazenar (opcional, padrão é 'true')
 */
export function setStorageFlag(key: string, value: string = 'true'): void {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    // Ignora erros silenciosamente
  }
}

/**
 * Verifica se um sinalizador está presente no sessionStorage
 * 
 * @param key Chave para verificar
 * @param value Valor esperado (opcional)
 * @returns {boolean} Verdadeiro se o sinalizador estiver presente com o valor esperado
 */
export function hasStorageFlag(key: string, value?: string): boolean {
  try {
    const storedValue = sessionStorage.getItem(key);
    
    if (value) {
      return storedValue === value;
    }
    
    return storedValue !== null;
  } catch (error) {
    return false;
  }
}

/**
 * Obtém o valor de um sinalizador do sessionStorage
 * 
 * @param key Chave para obter
 * @returns {string|null} Valor do sinalizador ou null se não existir
 */
export function getStorageFlag(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

/**
 * Remove um sinalizador do sessionStorage
 * 
 * @param key Chave a remover
 */
export function removeStorageFlag(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    // Ignora erros silenciosamente
  }
} 